import IconWithBackground from '../../components/IconWithBackground/IconWithBackground'
import { formatDate, openDownloadUrl } from '../../helpers/utils'
import { OrderDelivery } from '../../model'
import { getOrderDeliveryDownloadLink } from '../../services/api/deliveries'
import { useState } from 'react'
import DynamicFaIcon from '../../components/DynamicFaIcon/DynamicFaIcon'

interface DeliveryItemProps {
    data: OrderDelivery
    onDownload(): void
    onOrderDetail?: boolean
}

const DeliveryItem = ({ data, onDownload, onOrderDetail = false }: DeliveryItemProps) => {
    const [loading, setLoading] = useState<boolean>(false)

    const orderDate = formatDate(data.order?.orderDate, { year: 'numeric', month: 'long', day: '2-digit' })
    const deliveryDate = formatDate(data.deliveryDate, { year: 'numeric', month: 'long', day: '2-digit' })

    const handleDownload = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!data.id) return

        setLoading(true)

        try {
            const res = await getOrderDeliveryDownloadLink(data.id)
            const downloadLink = res?.downloadUrl

            openDownloadUrl(downloadLink)
            onDownload()
        } catch (error) {
            console.error(error)
        }

        setLoading(false)
    }

    return (
        <div
            className={`grid grid-cols-4 sm:grid-cols-10 md:grid-cols-8 relative items-center justify-between px-4 py-3 border-btm w-full gap-y-2 gap-x-2 ${
                !data.downloaded && 'newDelivery'
            }`}
            role='listitem'
        >
            <div className='grid grid-cols-1 sm:grid-cols-9 md:grid-cols-7 col-span-3 sm:col-span-9 md:col-span-7 items-center gap-y-2'>
                <div className='flex col-span-3 items-center gap-x-2 z-10'>
                    <IconWithBackground iconName='box' aria-label='Pakket icon' aria-hidden={false} />

                    <div className='flex flex-col'>
                        <span className={`font-semibold text-[--tb-text] ${!onOrderDetail ? 'text-sm' : 'text-base'}`}>
                            {data.contentTitle || data.order?.title}
                        </span>

                        {!onOrderDetail && (
                            <span className='text-xs text-[--tb-textLight]'>
                                <strong aria-label='Besteld op'>Besteld</strong>: {orderDate}
                            </span>
                        )}
                    </div>
                </div>

                {!onOrderDetail && (
                    <div className='col-span-3 md:col-span-2  items-center gap-x-2 flex '>
                        <IconWithBackground iconName='house' size='small' aria-label='Adres icon' aria-hidden={false} />
                        <div className='flex flex-col text-sm text-[--tb-text]'>
                            <span aria-label='Adres'>
                                {data.order?.address?.street} {data.order?.address?.houseNumber}
                                {data.order?.address?.extension}
                            </span>
                            <span aria-label='Postcode en plaats'>
                                {data.order?.address?.zipcode}, {data.order?.address?.city}
                            </span>
                        </div>
                    </div>
                )}

                <div className='col-span-3 md:col-span-2 gap-y-1 text-xs text-[--tb-text] flex flex-col flex-shrink-0'>
                    <div className='flex items-center'>
                        <DynamicFaIcon
                            aria-label='Opgeleverd op'
                            aria-hidden={false}
                            className={'mr-1.5'}
                            iconName='flag-checkered'
                        />
                        <span className='font-medium text-[--tb-text]'>Opgeleverd: {deliveryDate}</span>
                    </div>
                    <div className='flex items-center'>
                        <span
                            aria-label={data.downloaded ? 'Bestanden gedownload' : 'Bestanden nog niet gedownload'}
                            className={`${!data.downloaded && 'font-bold italic'}`}
                        >
                            <DynamicFaIcon
                                aria-hidden={false}
                                className={'mr-1.5 ' + (data.downloaded ? 'text-green-700' : 'text-red-600')}
                                iconName={data.downloaded ? 'check' : 'times'}
                            />
                            Bestanden {!data.downloaded && 'nog niet'} gedownload
                        </span>
                    </div>
                </div>
                {onOrderDetail && (
                    <div className='col-span-3 md:col-span-2 flex flex-row gap-y-1 text-xs text-[--tb-text] w-full items-center'>
                        {data.contentDescription && (
                            <>
                                <DynamicFaIcon iconName='comment' />
                                <span className='ml-2 text-xs text-[--tb-textLight]'>{data.contentDescription}</span>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className='flex-shrink-0 col-span-1 flex justify-end'>
                <button
                    onClick={handleDownload}
                    type='button'
                    aria-label='Download bestand'
                    aria-disabled={loading}
                    disabled={loading}
                    className={loading ? 'cursor-not-allowed' : ''}
                >
                    <IconWithBackground
                        iconName={loading ? 'spinner' : 'download'}
                        spin={loading}
                        size='small'
                        aria-label='Download bestand'
                        aria-hidden={false}
                    />
                </button>
            </div>
        </div>
    )
}

export default DeliveryItem
